export interface RefreshableAnalysisEntity {
  name: string;
  state?: string;
  imageUrls?: string[];
  selectedImageUrl?: string;
  refImageUrls?: string[];
  baseImageUrls?: string[];
  selectedBaseImageUrl?: string;
  baseRefImages?: string[];
}

export class LatestAuthorityRequestCoordinator {
  private sequence = 0;
  private activeScope: string | undefined;
  private latest?: { scope: string; sequence: number; outcome: Promise<boolean> };

  activate(scope: string): void {
    if (scope === this.activeScope) return;
    this.activeScope = scope;
    this.latest = undefined;
  }

  deactivate(scope: string): void {
    if (scope !== this.activeScope) return;
    this.activeScope = undefined;
    this.latest = undefined;
  }

  isActive(scope: string): boolean {
    return scope === this.activeScope;
  }

  run<T>(
    scope: string,
    load: () => Promise<T>,
    apply: (value: T) => boolean,
    onError: (error: unknown) => boolean = () => false,
  ): Promise<boolean> {
    if (!this.isActive(scope)) return Promise.resolve(false);
    const sequence = ++this.sequence;
    const followNewer = (): Promise<boolean> | undefined => {
      if (this.isActive(scope) && this.latest?.scope === scope && this.latest.sequence > sequence) {
        return this.latest.outcome;
      }
      return undefined;
    };
    const loaded = Promise.resolve().then(load);
    const outcome = loaded.then(
      value => {
        if (!this.isActive(scope)) return false;
        const newer = followNewer();
        if (newer) return newer;
        try {
          return apply(value);
        } catch {
          return false;
        }
      },
      error => {
        if (!this.isActive(scope)) return false;
        const newer = followNewer();
        if (newer) return newer;
        try {
          return onError(error);
        } catch {
          return false;
        }
      },
    );
    this.latest = { scope, sequence, outcome };
    return outcome;
  }
}

function cloneOptional(values: string[] | undefined): string[] | undefined {
  return values ? [...values] : undefined;
}

function mergeUrls(
  local: string[] | undefined,
  remote: string[] | undefined,
): string[] | undefined {
  if (!local || local.length === 0) return cloneOptional(remote);
  if (!remote || remote.length === 0) return [...local];

  const seen = new Set<string>();
  const merged: string[] = [];
  for (const url of remote) {
    if (!seen.has(url)) {
      seen.add(url);
      merged.push(url);
    }
  }
  if (merged.length >= local.length) return merged;
  for (const url of local) {
    if (!seen.has(url)) {
      seen.add(url);
      merged.push(url);
    }
  }
  return merged;
}

function copyRemote<T extends RefreshableAnalysisEntity>(remote: T): T {
  return {
    ...remote,
    imageUrls: cloneOptional(remote.imageUrls),
    refImageUrls: cloneOptional(remote.refImageUrls),
    baseImageUrls: cloneOptional(remote.baseImageUrls),
    baseRefImages: cloneOptional(remote.baseRefImages),
  };
}

export function mergeAnalysisEntities<T extends RefreshableAnalysisEntity>(
  previous: T[],
  remote: T[],
): T[] {
  const keyOf = (entity: T) => entity.state ? `${entity.name}|${entity.state}` : entity.name;
  const localByKey = new Map(previous.map(entity => [keyOf(entity), entity]));

  return remote.map(remoteEntity => {
    const local = localByKey.get(keyOf(remoteEntity));
    if (!local) return copyRemote(remoteEntity);

    return {
      ...remoteEntity,
      imageUrls: mergeUrls(local.imageUrls, remoteEntity.imageUrls),
      selectedImageUrl: remoteEntity.selectedImageUrl || local.selectedImageUrl,
      refImageUrls: mergeUrls(local.refImageUrls, remoteEntity.refImageUrls),
      baseImageUrls: cloneOptional(remoteEntity.baseImageUrls),
      selectedBaseImageUrl: remoteEntity.selectedBaseImageUrl,
      baseRefImages: cloneOptional(remoteEntity.baseRefImages),
    };
  });
}
